import {AbstractLitElement} from "../abstract-lit.element";
import {customElement} from "lit/decorators/custom-element.js";
import {html, HTMLTemplateResult} from "lit";
import {property} from "lit/decorators/property.js";
import {ClassInfo, classMap} from "lit/directives/class-map.js";
import {StringHelper} from "../../helpers/string.helper";
import {generateShortId} from "@o2d/shared";
import {SingleSelectionController, ITEM_SELECTED_EVENT} from "@o2d/popover-list";
import type {PopoverListItem, ItemSelectedDetail} from "@o2d/popover-list";

@customElement('ha-button-with-submenu')
export class HaButtonWithSubmenuComponent extends AbstractLitElement {

    @property({type: String, attribute: `button-title`})
    public buttonTitle!: string;

    @property({
        type: String,
        attribute: `submenu-layout`,
        converter: value => value === `buttons` ? `buttons` : `list`
    })
    public submenuLayout: string = `list`;

    @property({type: Array})
    public items: PopoverListItem[] = [];

    private _buttonClass: ClassInfo = {};
    private _popoverId: string = '';
    private _selectionController: SingleSelectionController | null = null;

    @property({type: String, attribute: `class`})
    public get buttonClass(): string {
        return Array.from(
            Object.entries(this._buttonClass)
        ).filter(([_, value]) => !!value).join(` `);
    }

    public set buttonClass(value: string) {
        this._buttonClass = Object.fromEntries(
            value.split(` `)
                .filter(key => StringHelper.isFilledString(key))
                .map(key => [key, true])
        );
    }

    /** @override */
    public connectedCallback(): void {
        super.connectedCallback();
        this.addEventListener(ITEM_SELECTED_EVENT, (evt: Event) =>
            this._onItemSelected(evt as CustomEvent<ItemSelectedDetail>)
        );
    }

    protected render(): HTMLTemplateResult {
        const popoverId = this._popoverId || `submenu-pending`;

        if (this.submenuLayout === `buttons`) {
            return html`
                <button type="button"
                        title=${this.buttonTitle}
                        class=${classMap(this._buttonClass)}
                        data-label=${this.buttonTitle}
                >
                    <div class="hover-effect"></div>
                </button>
                <o2d-popover-list id="${popoverId}" .items=${this.items}></o2d-popover-list>
            `;
        }

        // No `class=${classMap(this._buttonClass)}` here (unlike the "buttons"
        // branch above): that would mirror the HOST's class (e.g.
        // `sidebar-settings-link`, needed on the host for its own
        // flex/order positioning in todo_sidebar.css) onto this button too —
        // the exact same class on both the host and its child, flagged by
        // Anke (2026-08-19) as redundant. Styling reaches the button via a
        // `ha-button-with-submenu.<host-class> button` descendant selector
        // instead (todo_sidebar.css), so the button itself doesn't need the
        // class.
        return html`
            <button type="button"
                    title=${this.buttonTitle}
                    popovertarget="${popoverId}"
            >
                <div class="ha-app__navigation-item__prefix" title=${this.buttonTitle}></div>
                <div class="ha-app__navigation-item__label">${this.buttonTitle}</div>
            </button>
            <o2d-popover-list id="${popoverId}" popover="auto" .items=${this.items}></o2d-popover-list>
        `;
    }

    /** Sets up popover ID, anchor positioning, and SingleSelectionController. */
    public firstUpdated(): void {
        this._popoverId = `submenu-${generateShortId()}`;
        this.requestUpdate();

        const popover = this.renderRoot.querySelector('o2d-popover-list') as HTMLElement | null;
        const button = this.renderRoot.querySelector('button') as HTMLElement | null;

        if (popover && button) {
            button.style.setProperty('anchor-name', `--${this._popoverId}`);
        }

        if (popover) {
            this._selectionController = new SingleSelectionController(this, popover);

            if (this.submenuLayout === `buttons`) {
                // Remove popover attribute so the element stays in document flow.
                // o2d-popover-list auto-sets popover="auto" in connectedCallback — undo that.
                popover.removeAttribute('popover');
            }
        }
    }

    /** Handles item-selected event — calls payload function if present. */
    public _onItemSelected(evt: CustomEvent<ItemSelectedDetail>): void {
        const item = evt.detail?.item;
        if (item && typeof item.payload === 'function') {
            item.payload();
        }
    }
}
